import { Link } from '@tanstack/react-router';

import { CapacityMeter } from '@/features/concurrency';
import { PANEL } from '@/features/workspace-overview/stateStyles';

import { useOrganizationConcurrency } from '../hooks/useConcurrency';
import { installationLoad } from '../installationLoad';

/** Enough names to act on; the full list is the Limits page. */
const MAX_AT_LIMIT = 5;

/**
 * The installation's load, as the capacity meter (spec 116 US3, drawn since 118): how many runs execute
 * across every workspace against the ceiling, how many wait, and which workspaces
 * are held at their own limit — each a way into Runs at once.
 *
 * The same cached read as the Limits page, so no request of its own. Absent while
 * it loads, on failure, and against a service without the read: the console's
 * spend figures are the page's first job and must not wait on this.
 */
export function InstallationLoad() {
  const concurrency = useOrganizationConcurrency();
  if (!concurrency.data) return null;

  const load = installationLoad(concurrency.data);
  const shown = load.atLimit.slice(0, MAX_AT_LIMIT);
  const more = load.atLimit.length - shown.length;

  return (
    <section
      aria-label="Runs at once"
      // `shrink-0`: the console page is a flex column allowed to shrink its
      // children (`min-h-0`), and `PANEL` clips its overflow — together they
      // squashed this line to a sliver with its second row hidden.
      className={`${PANEL} flex shrink-0 flex-col items-start gap-1.5 px-4 py-3`}
    >
      {/* The workspace pages' meter, drawn for the ceiling (spec 118 US5). No
          popover: there is no one queue to open here, and the workspaces held at
          their limit are named just below. */}
      <CapacityMeter
        capacity={{
          running: load.running,
          limit: load.ceiling,
          queued: load.queued,
        }}
        label="Across the installation"
      />
      {shown.length > 0 ? (
        <p className="text-muted-foreground flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs">
          {/* "run limit", not "limit": the spend tiles above already have an
              "At their limit" tile, and it means money. */}
          <span>At their run limit:</span>
          {shown.map((workspace) => (
            <Link
              key={workspace.workspaceId}
              to="/admin/limits"
              search={{ workspace: workspace.workspaceId }}
              className="text-foreground font-medium underline underline-offset-2"
            >
              {workspace.name}
            </Link>
          ))}
          {more > 0 ? <span>+{String(more)} more</span> : null}
        </p>
      ) : null}
    </section>
  );
}
